import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Lock, LogOut, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { BRAND } from "@/constants/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

const formatDetail = (detail) => {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const statusColor = {
  pending: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  confirmed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  completed: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  cancelled: "border-rose-500/40 bg-rose-500/10 text-rose-300",
};

const inputClass = "border-emerald-500/20 bg-black/40 text-slate-100 placeholder:text-slate-600 focus-visible:ring-emerald-500";

export default function AdminPage() {
  const { t } = useLanguage();
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");

  const fetchBookings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bookings`, { withCredentials: true });
      setBookings(data);
    } catch {
      toast.error(t("admin.loadError"));
    }
  }, [t]);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) fetchBookings();
  }, [user, fetchBookings]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || t("admin.loginError"));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}`, { status }, { withCredentials: true });
      setBookings((b) => b.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(t("admin.updated"));
    } catch {
      toast.error(t("admin.loadError"));
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/bookings/${id}`, { withCredentials: true });
      setBookings((b) => b.filter((x) => x.id !== id));
      toast.success(t("admin.deleted"));
    } catch {
      toast.error(t("admin.loadError"));
    }
  };

  const serviceName = (id) => t("services.cards").find((s) => s.id === id)?.title || id;
  const filtered = bookings.filter((b) =>
    (statusFilter === "all" || b.status === statusFilter) && (serviceFilter === "all" || b.service === serviceFilter)
  );

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#030706]">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" data-testid="admin-loading" />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#030706] px-4">
        <div className="pointer-events-none absolute -top-40 left-1/2 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[140px]" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="shimmer-border w-full max-w-md rounded-2xl bg-[#08100d]/80 p-9 backdrop-blur-xl"
        >
          <div className="mb-8 flex flex-col items-center text-center">
            <img src={BRAND.logo} alt="New Era" className="h-14 w-14 rounded-xl object-cover ring-1 ring-emerald-500/30" />
            <h1 className="font-display mt-5 text-2xl text-slate-50">{t("admin.loginTitle")}</h1>
            <p className="mt-2 text-sm text-slate-400">{t("admin.loginSub")}</p>
          </div>
          <form data-testid="admin-login-form" onSubmit={login} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400">{t("admin.email")}</Label>
              <Input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputClass} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400">{t("admin.password")}</Label>
              <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={inputClass} />
            </div>
            <Button data-testid="admin-login-submit-button" type="submit" disabled={loading}
              className="glow-button w-full gap-2 border border-emerald-400/50 bg-emerald-500 py-6 text-sm font-semibold text-[#030706] hover:bg-emerald-400">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {loading ? t("admin.loggingIn") : t("admin.login")}
            </Button>
          </form>
          <Link data-testid="admin-back-link" to="/" className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500 transition-colors hover:text-emerald-400">
            <ArrowLeft className="h-3.5 w-3.5" /> {t("admin.back")}
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030706] text-slate-100">
      <header className="glass sticky top-0 z-40 border-b border-emerald-950/60">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="New Era" className="h-9 w-9 rounded-lg object-cover ring-1 ring-emerald-500/30" />
            <span className="font-display text-lg">{t("admin.dashboard")}</span>
          </div>
          <div className="flex items-center gap-3">
            <Link data-testid="admin-back-to-site" to="/" className="text-xs text-slate-400 transition-colors hover:text-emerald-400">{t("admin.back")}</Link>
            <Button data-testid="admin-logout-button" onClick={logout} size="sm" variant="outline"
              className="gap-2 border-rose-500/30 bg-transparent text-rose-300 hover:bg-rose-500/10">
              <LogOut className="h-3.5 w-3.5" /> {t("admin.logout")}
            </Button>
          </div>
        </div>
      </header>

      <main data-testid="admin-dashboard-panel" className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-5">
          {["total", ...STATUSES].map((key) => {
            const count = key === "total" ? bookings.length : bookings.filter((b) => b.status === key).length;
            return (
              <div key={key} data-testid={`admin-stat-${key}`} className="rounded-xl border border-emerald-500/15 bg-black/40 px-5 py-4">
                <div className="font-display text-2xl text-emerald-400">{count}</div>
                <div className="mt-1 text-[11px] uppercase tracking-wider text-slate-500">{t(`admin.${key}`)}</div>
              </div>
            );
          })}
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="flex flex-wrap gap-2">
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                data-testid={`admin-filter-${s}`}
                onClick={() => setStatusFilter(s)}
                className={`rounded-full px-4 py-1.5 font-tech text-xs transition-colors ${statusFilter === s ? "bg-emerald-500 text-[#030706]" : "border border-emerald-500/20 text-slate-400 hover:text-slate-200"}`}
              >
                {t(`admin.${s === "all" ? "all" : s}`)}
              </button>
            ))}
          </div>
          <Select value={serviceFilter} onValueChange={setServiceFilter}>
            <SelectTrigger data-testid="admin-service-filter" className={`w-56 ${inputClass}`}>
              <SelectValue placeholder={t("admin.filterService")} />
            </SelectTrigger>
            <SelectContent className="border-emerald-500/20 bg-[#08100d] text-slate-100">
              <SelectItem value="all">{t("admin.allServices")}</SelectItem>
              {t("services.cards").map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-emerald-500/15 bg-black/30">
          <table data-testid="admin-bookings-table" className="w-full min-w-[820px] text-left text-sm">
            <thead>
              <tr className="border-b border-emerald-500/10 font-tech text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-4">{t("admin.client")}</th>
                <th className="px-5 py-4">{t("admin.serviceCol")}</th>
                <th className="px-5 py-4">{t("admin.dateCol")}</th>
                <th className="px-5 py-4">{t("admin.contactCol")}</th>
                <th className="px-5 py-4">{t("admin.statusCol")}</th>
                <th className="px-5 py-4">{t("admin.actionsCol")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} data-testid="admin-bookings-empty" className="px-5 py-12 text-center text-slate-500">{t("admin.empty")}</td>
                </tr>
              )}
              {filtered.map((b) => (
                <tr key={b.id} data-testid="admin-booking-row-item" className="border-b border-emerald-500/5 transition-colors hover:bg-emerald-500/5">
                  <td className="px-5 py-4">
                    <div className="font-medium text-slate-100">{b.name}</div>
                    {b.notes && <div className="mt-1 max-w-[200px] truncate text-xs text-slate-500" title={b.notes}>{b.notes}</div>}
                  </td>
                  <td className="px-5 py-4 text-slate-300">{serviceName(b.service)}</td>
                  <td className="px-5 py-4 font-tech text-xs text-slate-300">{b.date} · {b.time}</td>
                  <td className="px-5 py-4">
                    <div className="text-xs text-slate-300">{b.email}</div>
                    <div className="text-xs text-slate-500">{b.phone}</div>
                  </td>
                  <td className="px-5 py-4">
                    <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                      <SelectTrigger data-testid="admin-status-toggle" className={`h-8 w-36 border text-xs ${statusColor[b.status]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-emerald-500/20 bg-[#08100d] text-slate-100">
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{t(`admin.${s}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-5 py-4">
                    <button data-testid="admin-delete-button" onClick={() => remove(b.id)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-500/30 text-rose-400 transition-colors hover:bg-rose-500/10"
                      aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
